import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Step,
  Steps,
  useToast,
  useDialogExitWarning,
  VisuallyHidden,
} from '@ais-ui/ui-core';
import { ReactElement, useEffect, useCallback } from 'react';
import { PiNotebook } from 'react-icons/pi';

import {
  ReportBackendTypes,
  useCreateReportTemplateAndReport,
} from '@ais-ui/websockets';
import { reportTypesObject } from '../../reportsTypes';
import type { ShapeType } from '../../reportsTypes';
import { FilterStep } from './FilterStep';
import { NamingStep } from './NamingStep';
import { PeriodStep } from './PeriodStep';
import { ReportTypesStep } from './ReportTypesStep';
import { ShapeStep } from './ShapeStep';
import { MiscellaneousStep } from './MiscellaneousStep';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';
import { useDrawingContext } from '@ais-ui/react-openlayers';
import { useReportContext } from '../../ReportContext';
import { ShipStep } from './ShipStep';
import { CreateModeSelection } from './CreateModeSelection';
import { QuickCreateForm } from './QuickCreateForm';
import { getReportErrorMessage } from '../utils/reportErrorHandler';
import { useSettings } from '../../../Settings/SettingsProvider';
import { buildReportFormModelFromContext } from '../utils/reportFormModel';
import { buildReportSubmissionFromModel } from '../utils/reportSubmission';
import { detectFilterTypeFromValue } from '../utils/filterTypeUtils';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface CreateReportTemplateProps {
  children: ReactElement;
}

export const CreateReportTemplate = ({
  children,
}: CreateReportTemplateProps) => {
  const { messages } = useLocalization();
  const { mutate } = useCreateReportTemplateAndReport();
  const { toast } = useToast();
  const { settings } = useSettings();

  const { geometryType } = useDrawingContext();
  const { setShouldRefetch } = useReportContext();
  const {
    showDialog,
    setShowDialog,
    setChosenReportType,
    steps,
    activeStep,
    chosenReportType,
    dateRange,
    reportTemplateName,
    firstReportName,
    shapeDictionary,
    filter,
    miscellaneous,
    resetSteps,
    currentStep,
    shapeTab,
    ships,
    shipAttribute,
    activeTab,
    setActiveTab,
    isDirty,
  } = useCreateReportContext();

  // Get the default template mode from settings
  const defaultTemplateMode =
    settings['ribbonPlugin.settings']?.report_default_template || 'wizard';

  // Sync activeTab with settings when dialog opens
  useEffect(() => {
    if (showDialog && !chosenReportType) {
      // Only reset to default when opening fresh (no report type chosen yet)
      const preferredTab = (
        defaultTemplateMode === 'quick' ? 'quick' : 'wizard'
      ) as 'wizard' | 'quick';
      if (activeTab !== preferredTab) {
        setActiveTab(preferredTab);
      }
    }
  }, [
    showDialog,
    chosenReportType,
    defaultTemplateMode,
    activeTab,
    setActiveTab,
  ]);

  useEffect(() => {
    if (activeStep !== steps.length) {
      return;
    }

    if (!chosenReportType) {
      return;
    }

    if (!dateRange || !dateRange.from || !dateRange.to) {
      return;
    }

    const model = buildReportFormModelFromContext({
      reportType: chosenReportType,
      reportTemplateName,
      reportName: firstReportName,
      dateRange: { from: dateRange.from, to: dateRange.to },
      miscellaneous,
      shapeDictionary,
      filter,
      shapeType: shapeTab as ShapeType,
      filterType: detectFilterTypeFromValue(filter),
      ships,
      shipAttribute,
    });

    if (!model) {
      return;
    }

    const submission = buildReportSubmissionFromModel(model);

    if (!submission) {
      return;
    }

    if (!submission.params) {
      return;
    }

    const callbacks = {
      onSuccess: () => {
        toast({
          className: 'create-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsCreateStartedSuccessMessage,
        });
        setShowDialog(false);
        setShouldRefetch(true);
      },
      onError: (error: Error) => {
        markErrorHandledLocally(error);
        const errorMessage = getReportErrorMessage(
          error,
          messages.reportsCreateStartedFailedMessage,
        );

        toast({
          className: 'create-report-error-toast',
          title: messages.genericToastTitleError,
          description: errorMessage,
          variant: 'destructive',
        });
        setShowDialog(false);
      },
    };
    mutate(
      { type: chosenReportType.type, params: submission.params },
      callbacks,
    );

    resetSteps();

    // We only want to run this if activeStep has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  let onPreviousNameStep:
    | ((event: React.MouseEvent<HTMLButtonElement>) => void)
    | null = null;

  if (chosenReportType !== reportTypesObject[ReportBackendTypes.REPLAY_VIDEO]) {
    onPreviousNameStep = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      resetSteps();
    };
  }

  useEffect(() => {
    // Do not reset when instant shape is being drawn
    if (!showDialog && !geometryType) {
      resetSteps();
    }
  }, [geometryType, resetSteps, showDialog]);

  const renderWizardSteps = () => {
    return (
      <div className="grid grid-rows-[auto_1fr] h-full">
        {currentStep && (
          <Steps className="h-16 my-6 flex-shrink-0" activeStep={activeStep}>
            {steps.map((step, index) => (
              <Step index={index} key={index} {...step}></Step>
            ))}
          </Steps>
        )}

        <div className="h-full overflow-auto px-px">
          {currentStep?.label === 'Name' && (
            <NamingStep onPrevious={onPreviousNameStep}></NamingStep>
          )}
          {currentStep?.label === 'Period' && <PeriodStep></PeriodStep>}
          {currentStep?.label === 'Ships' && <ShipStep></ShipStep>}
          {currentStep?.label === 'Filter' && <FilterStep></FilterStep>}
          {currentStep?.label === 'Shape' && <ShapeStep></ShapeStep>}
          {currentStep?.label === 'Miscellaneous' && (
            <MiscellaneousStep></MiscellaneousStep>
          )}
        </div>
      </div>
    );
  };

  const handleExitConfirm = useCallback(() => {
    setShowDialog(false);
    resetSteps();
  }, [setShowDialog, resetSteps]);

  const { handleClose, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: handleExitConfirm,
    isDirty: isDirty || chosenReportType !== null,
    title: messages.reportsCreateWarningTitle || 'Discard report?',
    description:
      messages.reportsCreateWarningDescription ||
      'Your report configuration will be lost. Are you sure you want to close?',
  });

  return (
    <>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent
          className="p-12 !w-full !max-w-3xl !min-w-[768px] h-[min(840px,90vh)] max-h-[90vh]"
          onClose={handleClose}
        >
          <VisuallyHidden>
            <DialogTitle>
              {chosenReportType
                ? messages.reportsCreateReportTypeTitle.replace(
                    '{label}',
                    chosenReportType.label,
                  )
                : messages.reportsCreateReportChooseReportTypeTitle}
            </DialogTitle>
            <DialogDescription>
              {chosenReportType
                ? messages.reportsCreateReportTypeDescription ||
                  'Configure and create your report'
                : messages.reportsCreateReportChooseReportTypeDescription ||
                  'Select a report type to begin'}
            </DialogDescription>
          </VisuallyHidden>
          <div className="flex flex-col h-full">
            <div className="pb-4 text-lg flex items-center justify-start space-x-4 h-12 flex-shrink-0">
              <PiNotebook className="h-6 w-6 text-primary"></PiNotebook>
              <div className="text-muted-foreground">
                {chosenReportType
                  ? messages.reportsCreateReportTypeTitle.replace(
                      '{label}',
                      chosenReportType.label,
                    )
                  : messages.reportsCreateReportChooseReportTypeTitle}
              </div>
            </div>

            {/* Show report type selection if no type is chosen */}
            {!chosenReportType && (
              <div className="flex-1 min-h-0 overflow-auto px-px">
                <ReportTypesStep setChosenReportType={setChosenReportType} />
              </div>
            )}

            {/* Show mode selection tabs after report type is chosen */}
            {chosenReportType && (
              <div className="flex-1 min-h-0 overflow-hidden px-px">
                <CreateModeSelection
                  wizardContent={renderWizardSteps()}
                  cloneContent={<QuickCreateForm />}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ExitWarningDialog />
    </>
  );
};
